import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {
  Button,
  DatePicker,
  Dropdown,
  Empty,
  Pagination,
  Select,
  Table,
  Tag,
} from 'antd';
import {
  ArrowLeftOutlined,
  CopyOutlined,
  DownloadOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SwapRightOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';

import {
  GET_ALL_SWAP_TRANSACTIONS,
  GET_MY_BULK_SWAP_JOBS,
  GET_SWAP_TRANSACTION_SUMMARY,
} from 'graphql/queries/wallet';
import {
  CREATE_USER_SWAP_TRANSACTION,
  EXPORT_SWAP_TRANSACTIONS,
} from 'graphql/mutations/wallet';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import ViewFilesModal from 'components/viewFilesModal';
import CreateSwapTransactionModal from 'components/createSwapTransactionModal';
import {
  buildSendNotificationMenuItem,
  useSendUserNotification,
} from 'components/sendUserNotification';

import SwapModal from './SwapModal';
import SwapTransactionCard from './SwapTransactionCard';
import SwapSummaryCards from './SwapSummaryCards';
import SwapBulkModal from './SwapBulkModal';
import SwapBulkRateModal from './SwapBulkRateModal';
import {SwapBulkJobDetails, SwapBulkJobsList} from './SwapBulkJob';
import DBModal from 'components/modal/modal';
import {buildSwapFilters, isJobTerminal} from './swapReporting';
import useIsCompact from 'utils/useIsCompact';
import styles from './swapTransactions.module.scss';
import {
  currencyName,
  currencyOptions,
  formatMoney,
  formatRate,
  formatRateValue,
  statusMeta,
  statusTagColor,
  SWAP_STATUSES,
  userFullName,
  userInitials,
} from 'utils/currencyFormatting';
import TableTopScrollbar from 'components/tableTopScrollbar';

const statusOptions = SWAP_STATUSES.map(({value, label}) => ({value, label}));

/** A filter that holds a value gets the brand outline, so it reads as applied. */
const filterFieldClass = value =>
  `${styles.filterField} ${value ? styles.filterFieldActive : ''}`;

const QUERY_OPTIONS = {
  notifyOnNetworkStatusChange: true,
  errorPolicy: 'all',
  fetchPolicy: 'no-cache',
};

const SwapTransactions = () => {
  const {currency} = useParams();
  const navigate = useNavigate();
  const isCompact = useIsCompact();

  const routeCurrency = currency && currency !== 'All' ? currency : undefined;
  // `?target=` lets a wallet transaction link straight to its own pair rather
  // than to every swap leaving the source currency. The swap filters accept
  // only source, target and status — there is no lookup by swap id or
  // reference — so the pair is as narrow as a link can get.
  const [searchParams] = useSearchParams();
  const routeTarget = searchParams.get('target') || undefined;
  const [filters, setFilters] = useState({
    sourceCurrency: routeCurrency,
    targetCurrency: routeTarget,
    status: undefined,
    // [from, to] local calendar days, either end optional.
    dates: undefined,
  });
  const [selectedIds, setSelectedIds] = useState([]);
  // The records behind every id ever ticked, across pages, so a bulk rate
  // update can check the selection shares one currency pair. Stale entries
  // are harmless: only ids still in `selectedIds` are ever read back.
  const selectedRecords = useRef({});
  const [bulkRequest, setBulkRequest] = useState(null);
  const [bulkRateRequest, setBulkRateRequest] = useState(null);
  // Recent bulk jobs: null, {mode: 'list'} or {mode: 'job', job}.
  const [jobsPanel, setJobsPanel] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [updateModal, setUpdateModal] = useState({});
  const [viewFilesModal, setViewFilesModal] = useState({
    isOpen: false,
    files: [],
  });

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Swap),
  );

  const {canSendNotification, openSendNotification, sendNotificationModal} =
    useSendUserNotification();
  const role = useSelector(state => state?.auth?.userData?.user);
  const isSuperOrAdmin = ['SUPERADMIN', 'ADMIN'].includes(
    role?.activeAdminTab?.name,
  );
  const canCreate = !!tabOperations?.create && isSuperOrAdmin;

  const {data, loading, refetch} = useQuery(GET_ALL_SWAP_TRANSACTIONS, {
    variables: {
      input: {...buildSwapFilters(filters), pagination: {page, limit}},
    },
    onError: error => toast.error(error.message),
    ...QUERY_OPTIONS,
  });

  // The cards leave status out, so paging and switching tabs never refetch
  // them; only a currency or date change (or an action) does.
  const {
    data: summaryData,
    loading: loadingSummary,
    error: summaryNetworkError,
    refetch: refetchSummary,
  } = useQuery(GET_SWAP_TRANSACTION_SUMMARY, {
    variables: {input: buildSwapFilters({...filters, status: undefined})},
    ...QUERY_OPTIONS,
  });
  const summaryResult = summaryData?.getSwapTransactionSummary;
  const summary =
    summaryResult?.__typename === 'SwapTransactionSummary'
      ? summaryResult
      : null;
  const summaryError =
    summaryResult?.__typename === 'Error'
      ? summaryResult.message
      : summaryNetworkError
      ? 'Could not load the totals.'
      : null;

  const refetchAll = useCallback(() => {
    refetch();
    refetchSummary();
  }, [refetch, refetchSummary]);

  const [exportSwaps, {loading: exporting}] = useMutation(
    EXPORT_SWAP_TRANSACTIONS,
  );
  const canExport = !!tabOperations?.export;
  const canBulk = !!tabOperations?.update;

  // The admin's recent bulk jobs. Re-read only while one is still queued or
  // running, so a job finishing — even after its dialog was closed, or on a
  // later visit — refreshes the page.
  const {
    data: jobsData,
    refetch: refetchJobs,
    startPolling: startJobsPolling,
    stopPolling: stopJobsPolling,
  } = useQuery(GET_MY_BULK_SWAP_JOBS, {
    variables: {limit: 20},
    skip: !canBulk,
    fetchPolicy: 'network-only',
  });
  const activeJobs = (jobsData?.getMyBulkSwapTransactionJobs || []).filter(
    job => !isJobTerminal(job),
  );
  const activeKey = activeJobs.map(job => job.jobId).join(',');
  useEffect(() => {
    if (activeKey) startJobsPolling(5000);
    else stopJobsPolling();
    return stopJobsPolling;
  }, [activeKey, startJobsPolling, stopJobsPolling]);

  // A job that was running and is not any more has moved wallet balances.
  const wasActive = useRef('');
  useEffect(() => {
    const before = wasActive.current.split(',').filter(Boolean);
    const now = activeKey.split(',');
    if (before.some(id => !now.includes(id))) {
      setSelectedIds([]);
      refetchAll();
    }
    wasActive.current = activeKey;
  }, [activeKey, refetchAll]);

  const onJobFinished = useCallback(() => {
    setSelectedIds([]);
    refetchAll();
    refetchJobs?.();
  }, [refetchAll, refetchJobs]);

  const [swapCurrencies, {loading: loadingSwap}] = useMutation(
    CREATE_USER_SWAP_TRANSACTION,
  );

  const result = data?.getAllSwapTransactions;
  const transactions = result?.swapTransactions ?? [];
  const total = result?.paginate?.total ?? 0;

  // "No filters" means the scope the URL asked for, so clearing on
  // /swap-transactions/NGN returns to NGN rather than to every currency.
  const hasFilters =
    filters.sourceCurrency !== routeCurrency ||
    !!filters.targetCurrency ||
    !!filters.status ||
    !!(filters.dates?.[0] || filters.dates?.[1]);

  // One set of props for both pagers — the sticky one above the table and
  // the one below it — so they can never disagree about page or size.
  const pagerProps = {
    current: page,
    pageSize: limit,
    total,
    showQuickJumper: true,
    showSizeChanger: true,
    size: isCompact ? 'small' : 'default',
    // Both size and page changes are handled here on purpose. antd fires
    // onShowSizeChange first and then onChange(current, size), so resetting
    // the page inside onShowSizeChange gets overwritten a moment later and
    // the operator stays on whatever page they were on. Reading the size out
    // of onChange keeps the reset.
    onChange: (nextPage, nextSize) => {
      if (nextSize !== limit) {
        setLimit(nextSize);
        setPage(1);
        return;
      }
      setPage(nextPage);
    },
  };

  // A selection is a set of rows on screen, so it goes when the rows change.
  const updateFilter = useCallback((key, value) => {
    setPage(1);
    setSelectedIds([]);
    setFilters(prev => ({...prev, [key]: value || undefined}));
  }, []);

  const handleExport = async () => {
    try {
      // Pagination is left out: an export is every matching row.
      const {data: exported} = await exportSwaps({
        variables: {input: buildSwapFilters(filters)},
      });
      const file = exported?.exportSwapTransactions;
      if (file?.__typename !== 'SwapExportFile') {
        toast.error(file?.message || 'Could not export the swaps.');
        return;
      }
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.fileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(
        `Exported ${file.count.toLocaleString('en-US')} swap${
          file.count === 1 ? '' : 's'
        }. The link works for two hours.`,
      );
    } catch (error) {
      toast.error(error.message || 'Could not export the swaps.');
    }
  };

  // A bulk update takes the page's currencies; its dates are chosen in the
  // dialog itself, so a run is never narrowed by a date filter left on here.
  const openBulkAll = status =>
    setBulkRequest({
      status,
      allMatching: true,
      currencies: {
        sourceCurrency: filters.sourceCurrency,
        targetCurrency: filters.targetCurrency,
      },
    });
  const bulkAllItems = [
    {
      key: 'fulfil-all',
      label: 'Fulfil pending swaps…',
      onClick: () => openBulkAll('SUCCESSFUL'),
    },
    {
      key: 'reject-all',
      danger: true,
      label: 'Reject pending swaps…',
      onClick: () => openBulkAll('FAILED'),
    },
    {type: 'divider', key: 'rate-divider'},
    // One rate only means something for one pair, so this needs both
    // currencies filtered on the page.
    {
      key: 'rate-all',
      label:
        filters.sourceCurrency && filters.targetCurrency
          ? 'Update rate on pending swaps…'
          : 'Update rate on pending swaps… (filter a source and target first)',
      disabled: !(filters.sourceCurrency && filters.targetCurrency),
      onClick: () =>
        setBulkRateRequest({
          allMatching: true,
          currencies: {
            sourceCurrency: filters.sourceCurrency,
            targetCurrency: filters.targetCurrency,
          },
        }),
    },
  ];

  const copyReference = useCallback(reference => {
    if (!reference) return;
    navigator.clipboard
      ?.writeText(reference)
      .then(() => toast.success('Reference ID copied'))
      .catch(() => toast.error('Could not copy the reference ID'));
  }, []);

  /**
   * One menu for both renderings — the table's pinned cell and the mobile card
   * offer exactly the same actions.
   */
  const buildActionItems = useCallback(
    record => {
      const files = record?.metadata?.files;
      const canAct = record?.status === 'PENDING' && !!tabOperations?.update;
      const items = [];

      if (canAct) {
        items.push({
          key: 'fulfil',
          label: 'Fulfil or reject',
          onClick: () =>
            setUpdateModal({
              open: true,
              id: record?.id,
              status: record?.status,
              sourceCurrency: record?.sourceCurrency?.currency,
              targetCurrency: record?.targetCurrency?.currency,
              amountPaid: record?.amountPaid,
              amountReceived: record?.amountReceived,
              user: record?.user,
              files: record?.metadata?.files,
            }),
        });
        items.push({
          key: 'rate',
          label: 'Update rate and files',
          onClick: () =>
            setUpdateModal({
              updateOpen: true,
              id: record?.id,
              status: record?.status,
              targetCurrency: record?.targetCurrency?.currency,
              sourceCurrency: record?.sourceCurrency?.currency,
              rate: record?.rate,
              amountPaid: record?.amountPaid,
              user: record?.user,
              files: record?.metadata?.files,
            }),
        });
      }

      if (files?.length) {
        items.push({
          key: 'files',
          label: `View files (${files.length})`,
          onClick: () => setViewFilesModal({isOpen: true, files}),
        });
      }

      if (record?.referenceId) {
        items.push({
          key: 'reference',
          icon: <CopyOutlined />,
          label: 'Copy reference ID',
          onClick: () => copyReference(record.referenceId),
        });
      }

      const notifyItem = buildSendNotificationMenuItem({
        user: record?.user,
        context: {
          label: 'Swap transaction',
          reference: record?.referenceId,
        },
        onOpen: openSendNotification,
        allowed: canSendNotification,
      });
      if (notifyItem) items.push(notifyItem);

      return items;
    },
    [
      copyReference,
      tabOperations?.update,
      canSendNotification,
      openSendNotification,
    ],
  );

  const columns = useMemo(
    () => [
      {
        title: 'User',
        key: 'user',
        fixed: 'left',
        width: 225,
        render: (_, record) => {
          const name = userFullName(record?.user) || 'Unknown user';
          const userId = record?.user?.id;
          const identity = (
            <span className={styles.identity}>
              <span className={styles.avatar}>
                {userInitials(record?.user)}
              </span>
              <span className={styles.identityText}>
                <span className={styles.nameCell}>{name}</span>
                <span className={styles.emailCell}>
                  {record?.user?.email || '—'}
                </span>
              </span>
            </span>
          );

          if (!userId) return identity;
          return (
            <a
              href={`/dashboard/user/${userId}/wallet-settings`}
              target="_blank"
              rel="noreferrer"
              className={styles.userNameLink}
              onClick={event => event.stopPropagation()}>
              {identity}
            </a>
          );
        },
      },
      {
        title: 'Pair',
        key: 'pair',
        width: 145,
        render: (_, record) => {
          const source = record?.sourceCurrency?.currency;
          const target = record?.targetCurrency?.currency;
          return (
            <div className={styles.pairCell}>
              <span className={styles.pairLine}>
                <span className={styles.pairCurrency}>{source}</span>
                <SwapRightOutlined className={styles.pairArrow} />
                <span className={styles.pairCurrency}>{target}</span>
              </span>
              <span className={styles.pairRate}>
                {formatRate(record?.rate, source, target)}
              </span>
            </div>
          );
        },
      },
      // Fee rides along in these two cells rather than taking a column of its
      // own: together they read "paid $200, $4 of it fee, so $196 converted
      // into ₦253,428" without pushing Status and Date off the screen.
      {
        title: 'From',
        key: 'amountPaid',
        width: 150,
        align: 'right',
        render: (_, record) => {
          const source = record?.sourceCurrency?.currency;
          return (
            <div className={styles.amountCell}>
              <span className={styles.money}>
                {formatMoney(record?.amountPaid, source)}
              </span>
              <span className={styles.amountHint}>
                incl. {formatMoney(record?.calculatedFees, source)} fee
              </span>
            </div>
          );
        },
      },
      {
        title: 'Into',
        key: 'amountReceived',
        width: 155,
        align: 'right',
        render: (_, record) => (
          <div className={styles.amountCell}>
            <span className={`${styles.money} ${styles.received}`}>
              {formatMoney(
                record?.amountReceived,
                record?.targetCurrency?.currency,
                {decimals: 2},
              )}
            </span>
            <span className={styles.amountHint}>
              from{' '}
              {formatMoney(
                record?.amountSwapped,
                record?.sourceCurrency?.currency,
              )}
            </span>
          </div>
        ),
      },
      {
        title: 'Status',
        key: 'status',
        width: 110,
        render: (_, record) => (
          <Tag
            className={styles.statusTag}
            color={statusTagColor(record?.status)}>
            {statusMeta(record?.status).label}
          </Tag>
        ),
      },
      {
        title: 'Date',
        key: 'createdAt',
        width: 130,
        render: (_, record) => (
          <span className={styles.date}>
            {moment(record?.createdAt).format('DD MMM YYYY')}
            <span className={styles.dateTime}>
              {moment(record?.createdAt).format('h:mm A')}
            </span>
          </span>
        ),
      },
      {
        title: '',
        key: 'actions',
        fixed: 'right',
        width: 56,
        render: (_, record) => {
          const items = buildActionItems(record);
          if (!items.length) return <span className={styles.noActions}>—</span>;
          return (
            <div
              className={styles.actionsCell}
              onClick={event => event.stopPropagation()}>
              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                menu={{items}}>
                <button
                  type="button"
                  aria-label="Transaction actions"
                  className={styles.actionsTrigger}>
                  <MoreOutlined />
                </button>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [buildActionItems],
  );

  /** The detail an operator only needs occasionally, folded out of the row. */
  const expandedRowRender = record => {
    const source = record?.sourceCurrency?.currency;
    const target = record?.targetCurrency?.currency;
    const files = record?.metadata?.files ?? [];

    return (
      <div className={styles.expanded}>
        <div className={styles.expandedItem}>
          <span className={styles.expandedLabel}>Reference ID</span>
          <span className={styles.expandedValue}>
            {record?.referenceId || '—'}
          </span>
        </div>
        <div className={styles.expandedItem}>
          <span className={styles.expandedLabel}>Lock ID</span>
          <span className={styles.expandedValue}>{record?.lockId || '—'}</span>
        </div>
        <div className={styles.expandedItem}>
          <span className={styles.expandedLabel}>Exact rate</span>
          <span className={styles.expandedValue}>
            {formatRateValue(record?.rate)} {target} per 1 {source}
          </span>
        </div>
        <div className={styles.expandedItem}>
          <span className={styles.expandedLabel}>Source currency</span>
          <span className={styles.expandedValue}>
            {source} — {currencyName(source)}
          </span>
        </div>
        <div className={styles.expandedItem}>
          <span className={styles.expandedLabel}>Target currency</span>
          <span className={styles.expandedValue}>
            {target} — {currencyName(target)}
          </span>
        </div>
        <div className={styles.expandedItem}>
          <span className={styles.expandedLabel}>Attached files</span>
          {files.length ? (
            <Button
              type="link"
              className={styles.inlineLink}
              onClick={() => setViewFilesModal({isOpen: true, files})}>
              View {files.length} file{files.length === 1 ? '' : 's'}
            </Button>
          ) : (
            <span className={styles.expandedValue}>None</span>
          )}
        </div>
      </div>
    );
  };

  const emptyState = (
    <div className={styles.emptyState}>
      <h3>No swap transactions</h3>
      <p>
        {hasFilters
          ? 'Nothing matches the filters above. Try clearing them.'
          : 'Swaps created by users or by an admin will show up here.'}
      </p>
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleWrap}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate('/dashboard/wallet')}>
            <ArrowLeftOutlined /> Wallet
          </button>
          <h1 className={styles.title}>Swap transactions</h1>
          <p className={styles.subtitle}>
            {routeCurrency
              ? `Swaps leaving ${routeCurrency} — ${currencyName(
                  routeCurrency,
                )}.`
              : 'Every currency swap on the platform.'}{' '}
            Review a pending swap, correct its rate, or record one manually.
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button
            icon={<ReloadOutlined />}
            onClick={refetchAll}
            loading={loading}>
            Refresh
          </Button>
          <Button
            icon={<DownloadOutlined />}
            loading={exporting}
            disabled={!canExport}
            title={
              canExport
                ? 'Download every swap matching the filters as a CSV'
                : 'You need export permission on Swap to download swaps'
            }
            onClick={handleExport}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className={styles.primaryButton}
            disabled={!canCreate}
            title={
              canCreate
                ? undefined
                : 'You need admin create permission to record a swap manually'
            }
            onClick={() => setOpenCreateModal(true)}>
            New manual swap
          </Button>
        </div>
      </div>

      <SwapSummaryCards
        summary={summary}
        loading={loadingSummary}
        error={summaryError}
        activeStatus={filters.status}
        onSelect={status => updateFilter('status', status)}
      />

      <div className={styles.filterBar}>
        <div className={styles.filterFields}>
          <Select
            allowClear
            className={filterFieldClass(filters.sourceCurrency)}
            placeholder="Source currency"
            value={filters.sourceCurrency}
            options={currencyOptions}
            onChange={value => updateFilter('sourceCurrency', value)}
          />
          <Select
            allowClear
            className={filterFieldClass(filters.targetCurrency)}
            placeholder="Target currency"
            value={filters.targetCurrency}
            options={currencyOptions}
            onChange={value => updateFilter('targetCurrency', value)}
          />
          {/* Same `filters.status` the cards above drive, so picking a status
              here lights up its card and vice versa. */}
          <Select
            allowClear
            className={filterFieldClass(filters.status)}
            placeholder="Status"
            value={filters.status}
            options={statusOptions}
            onChange={value => updateFilter('status', value)}
          />
          <DatePicker.RangePicker
            allowEmpty={[true, true]}
            className={`${styles.dateFilter} ${
              filters.dates?.[0] || filters.dates?.[1]
                ? styles.filterFieldActive
                : ''
            }`}
            placeholder={['Created from', 'to']}
            format="D MMM YYYY"
            value={filters.dates}
            onChange={value => updateFilter('dates', value)}
          />
        </div>
        {/* Clear and the count share a group, so the "Clear filters" link
            appearing cannot squeeze a select onto a new row — this whole
            group wraps below the selects instead. */}
        <div className={styles.filterMeta}>
          {hasFilters && (
            <Button
              type="link"
              className={styles.clearButton}
              onClick={() => {
                setFilters({sourceCurrency: routeCurrency});
                setSelectedIds([]);
                setPage(1);
              }}>
              Clear filters
            </Button>
          )}
          <span className={styles.resultCount}>
            {loading
              ? 'Loading…'
              : `${total.toLocaleString('en-US')} transaction${
                  total === 1 ? '' : 's'
                }${hasFilters ? ' matching filters' : ''}`}
          </span>
        </div>
      </div>

      {/* Pinned copy of the pager below the table, so paging does not mean
          scrolling to the last row and back. A sibling of the table container
          on purpose: that element is overflow:hidden, which would confine the
          sticky positioning to it. */}
      {canBulk && (
        <div className={styles.bulkBar}>
          {selectedIds.length ? (
            <>
              <span className={styles.bulkCount}>
                {selectedIds.length} pending swap
                {selectedIds.length === 1 ? '' : 's'} selected
              </span>
              <Button
                type="primary"
                className={styles.primaryButton}
                onClick={() =>
                  setBulkRequest({status: 'SUCCESSFUL', ids: selectedIds})
                }>
                Fulfil selected
              </Button>
              <Button
                danger
                onClick={() =>
                  setBulkRequest({status: 'FAILED', ids: selectedIds})
                }>
                Reject selected
              </Button>
              <Button
                onClick={() =>
                  setBulkRateRequest({
                    ids: selectedIds,
                    rows: selectedIds
                      .map(id => selectedRecords.current[id])
                      .filter(Boolean),
                  })
                }>
                Update rate for selected
              </Button>
              <Button type="link" onClick={() => setSelectedIds([])}>
                Clear selection
              </Button>
            </>
          ) : (
            <span className={styles.bulkHint}>
              Tick pending swaps to fulfil, reject or re-rate them together, or
              act on every pending swap matching the filters.
            </span>
          )}
          <div className={styles.bulkEnd}>
            {activeJobs.length > 0 && (
              <button
                type="button"
                className={styles.jobRunning}
                onClick={() =>
                  setJobsPanel(
                    activeJobs.length === 1
                      ? {mode: 'job', job: activeJobs[0]}
                      : {mode: 'list'},
                  )
                }>
                <span className={styles.jobPulse} />
                {activeJobs.length} bulk job
                {activeJobs.length === 1 ? '' : 's'} running
              </button>
            )}
            <Button onClick={() => setJobsPanel({mode: 'list'})}>
              Recent bulk jobs
            </Button>
            <Dropdown trigger={['click']} menu={{items: bulkAllItems}}>
              <Button>Bulk update pending</Button>
            </Dropdown>
          </div>
        </div>
      )}

      {total > 0 && <TableTopScrollbar pagerProps={pagerProps} />}

      {isCompact ? (
        <div className={styles.cardList}>
          {loading && !transactions.length ? (
            <div className={styles.cardsLoading}>Loading transactions…</div>
          ) : transactions.length ? (
            transactions.map(record => (
              <SwapTransactionCard
                key={record?.id}
                record={record}
                actionItems={buildActionItems(record)}
              />
            ))
          ) : (
            <Empty
              description={emptyState}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <Table
            rowKey="id"
            columns={columns}
            loading={loading}
            dataSource={transactions}
            pagination={false}
            // A number, not 'max-content': max-content lets a long email widen
            // the user column past its declared width and pushes Date off the
            // screen. Pinning the total makes the declared widths bind, so the
            // email ellipsises instead. Keep in step with the column widths.
            scroll={{x: 1007}}
            expandable={{expandedRowRender, fixed: 'left', columnWidth: 40}}
            rowSelection={
              canBulk
                ? {
                    selectedRowKeys: selectedIds,
                    // Kept across pages: a selection can span several.
                    preserveSelectedRowKeys: true,
                    onChange: (keys, rows) => {
                      rows.forEach(row => {
                        if (row?.id) selectedRecords.current[row.id] = row;
                      });
                      setSelectedIds(keys);
                    },
                    getCheckboxProps: record => ({
                      disabled: record?.status !== 'PENDING',
                      'aria-label': 'Select swap',
                    }),
                  }
                : undefined
            }
            // Blank while loading, so "no swaps" never shows behind the
            // spinner of a list that is about to fill.
            locale={{emptyText: loading ? ' ' : emptyState}}
          />
        </div>
      )}

      {total > 0 && (
        <div className={styles.paginationBar}>
          <Pagination {...pagerProps} />
        </div>
      )}

      <SwapModal
        updateModal={updateModal}
        setUpdateModal={setUpdateModal}
        refetch={refetchAll}
      />
      <ViewFilesModal
        isOpen={viewFilesModal.isOpen}
        onClose={() => setViewFilesModal({isOpen: false, files: []})}
        files={viewFilesModal.files}
      />
      <CreateSwapTransactionModal
        isOpen={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        mutation={swapCurrencies}
        refetch={refetchAll}
        loading={loadingSwap}
      />
      <SwapBulkModal
        request={bulkRequest}
        onClose={() => setBulkRequest(null)}
        // A started (or possibly started) job shows up in Recent bulk jobs.
        onStarted={() => refetchJobs?.()}
        onFinished={onJobFinished}
        onShowRecentJobs={() => {
          setBulkRequest(null);
          setJobsPanel({mode: 'list'});
        }}
      />
      <SwapBulkRateModal
        request={bulkRateRequest}
        onClose={() => setBulkRateRequest(null)}
        // Rates changed on the rows shown, so the list and the totals re-read.
        onDone={() => {
          setSelectedIds([]);
          refetchAll();
        }}
      />
      <DBModal
        isOpen={Boolean(jobsPanel)}
        width={jobsPanel?.mode === 'job' ? 560 : 760}
        destroyOnClose
        handleClose={() => setJobsPanel(null)}
        content={
          <div className={styles.bulkModal}>
            <h2>
              {jobsPanel?.mode === 'job' ? 'Bulk job' : 'Recent bulk jobs'}
            </h2>
            {jobsPanel?.mode === 'job' ? (
              <>
                <SwapBulkJobDetails
                  jobId={jobsPanel.job.jobId}
                  initialJob={jobsPanel.job}
                  onTerminal={onJobFinished}
                />
                <div className={styles.bulkActions}>
                  <Button onClick={() => setJobsPanel({mode: 'list'})}>
                    All recent jobs
                  </Button>
                  <Button type="primary" onClick={() => setJobsPanel(null)}>
                    Close
                  </Button>
                </div>
              </>
            ) : (
              <SwapBulkJobsList
                onOpen={job => setJobsPanel({mode: 'job', job})}
              />
            )}
          </div>
        }
      />
      {sendNotificationModal}
    </div>
  );
};

export default SwapTransactions;
